import { Spacing } from '@persona-id/design-tokens';
import { compact, last, uniq, uniqBy } from 'lodash/fp';
import { ComponentProps, FC, FocusEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  components as ReactSelectComponents,
  GroupBase,
  InputProps,
  StylesConfig,
} from 'react-select';

import SelectOption, { ObjectKeySuffix } from 'dashboard/components/JsonSchemaSelect/SelectOption';
import {
  getOptionForValue,
  getSchemaOptions,
  Option,
  OptionFilters,
  validatePath,
} from 'dashboard/components/JsonSchemaSelect/utils';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import { valueTransformer } from 'dashboard/lib/workflows';
import { methodChainToString, parseWithPartialOption } from 'dashboard/lib/workflows/method-chain';
import {
  usePlatformObjectJsonSchema,
  usePrimitiveUtilities,
  useSchemaForFieldPath,
} from 'dashboard/redux/selectors/workflow-editor-context';
import { JsonSchemaAllTypes, JsonSchemaNumberTypes, JsonSchemaStringTypes } from 'lib/fields';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';

const customStyles = ({ isClearable }: { isClearable: boolean }): StylesConfig => ({
  control: () => ({
    height: 'auto',
  }),
  dropdownIndicator: () => ({
    display: 'none',
  }),
  ...(isClearable
    ? {}
    : {
        indicatorsContainer: () => ({
          display: 'none',
        }),
      }),
  multiValueLabel: () => ({
    padding: '2px',
  }),
  valueContainer: (_, { isMulti }) => ({
    height: 'auto',
    minHeight: '30px',
    maxHeight: '80px',
    overflowY: 'auto',
    paddingTop: isMulti ? Spacing[1] : 0,
    paddingBottom: isMulti ? Spacing[1] : 0,
  }),
});

const SelectInput = (
  props: InputProps<OptionType<string>, boolean, GroupBase<OptionType<string>>> & {
    selectProps: ComponentProps<typeof ReactSelect<string>>;
  },
) => {
  let id = props.id;
  let listboxId = props['aria-owns'];

  if (props.selectProps.id) {
    if (!props.selectProps.inputId) {
      id = `${props.selectProps.id}-input`;
    }
    listboxId = `${props.selectProps.id}-listbox`;
  }

  const ariaControls = listboxId;
  const ariaOwns = listboxId;

  return (
    <ReactSelectComponents.Input
      {...props}
      isHidden={false}
      // This value isn't applied reactively. We need to update ReactSelect's custom component structure
      // So keeps an initial value...
      aria-invalid={props.selectProps.selectHasError}
      id={id}
      aria-controls={ariaControls}
      aria-owns={ariaOwns}
    />
  );
};

interface JsonSchemaSelectProps {
  jsonSchema: JsonSchema;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  autoFocus?: boolean;
  canSelectObjects?: boolean;
  disabled?: boolean;
  exampleData?: Record<string, unknown>;
  hasError?: boolean;
  required?: boolean;
  id?: string;
  isClearable?: boolean;
  inputValue: string | string[];
  portal?: boolean;
  size?: 'sm' | 'md';
  styles?: Partial<StylesConfig>;
  tokensToDisplayValue: Record<string, string>;
  useStrictTypeMatch?: boolean;
  onFieldSelect: (data: Option | Option[]) => void;
  onValueChange: (value: string | string[]) => void;
  onBlur?: () => void;
  closeDropdownOnBlur?: boolean;
}

const JsonSchemaSelect: FC<JsonSchemaSelectProps & OptionFilters> = ({
  allowedArrayTypes,
  allowedFieldPaths,
  allowedTypes: passedAllowedTypes,
  disallowedFieldPaths,
  fieldPathsAreStrictMatch,
  exampleData,
  inputValue: passedInputValue,
  jsonSchema,
  tokensToDisplayValue,
  ariaLabel = 'Select JSON schema field',
  ariaLabelledBy,
  autoFocus = false,
  canSelectObjects = true,
  disabled = false,
  hasError = false,
  required = true,
  id = 'json-schema-select__input',
  isClearable = false,
  size = 'md',
  styles = {},
  useStrictTypeMatch = false,
  onFieldSelect,
  onValueChange,
  onBlur,
  closeDropdownOnBlur = false,
}) => {
  const platformObjectJsonSchema = usePlatformObjectJsonSchema();
  const primitiveUtilities = usePrimitiveUtilities();
  const schemaForFieldPath = useSchemaForFieldPath();

  const allowedTypes = useMemo(() => {
    if (!passedAllowedTypes || useStrictTypeMatch) {
      return passedAllowedTypes;
    }
    return uniq(
      passedAllowedTypes.flatMap((type) => {
        if (JsonSchemaStringTypes.includes(type as JsonSchemaAllTypes)) {
          return JsonSchemaStringTypes;
        }
        if (JsonSchemaNumberTypes.includes(type as JsonSchemaTypes)) {
          return JsonSchemaNumberTypes;
        }
        return [type];
      }),
    );
  }, [passedAllowedTypes, useStrictTypeMatch]);

  const vt = useMemo(() => valueTransformer(tokensToDisplayValue), [tokensToDisplayValue]);
  const isMulti = Array.isArray(passedInputValue);
  const inputValue = isMulti
    ? (passedInputValue as string[]).map(vt.getDisplayValue)
    : passedInputValue
      ? vt.getDisplayValue(passedInputValue as string)
      : '';

  const [showDropdown, setShowDropdown] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [schemaOptions, setSchemaOptions] = useState([]);

  const { url: currentUrl } = useRouteMatch();
  // TODO @inquiries - temporary solution to ignore validation for ITE
  const ignoreValidation = currentUrl.includes('/dashboard/inquiry-templates/');

  const wrappedGetOptionForValue = useCallback(
    (value: string) =>
      getOptionForValue({
        value,
        jsonSchema,
        schemaForFieldPath,
      }),
    [jsonSchema, schemaForFieldPath],
  );
  const wrappedSetIsValid = useCallback(
    (valid: boolean) => setIsValid(ignoreValidation || valid),
    [ignoreValidation],
  );
  const getOptionFilters = useCallback(
    (value: string | string[]) => {
      const allDisallowedFieldPaths = Array.isArray(value)
        ? [...value, ...(disallowedFieldPaths || [])]
        : disallowedFieldPaths;
      return {
        allowedArrayTypes,
        allowedFieldPaths: allowedFieldPaths?.map(vt.getDisplayValue),
        allowedTypes,
        disallowedFieldPaths: allDisallowedFieldPaths?.map(vt.getDisplayValue),
        fieldPathsAreStrictMatch,
      };
    },
    [
      allowedArrayTypes,
      allowedFieldPaths,
      allowedTypes,
      disallowedFieldPaths,
      fieldPathsAreStrictMatch,
      vt.getDisplayValue,
    ],
  );

  const optionFilters = useMemo(() => getOptionFilters(inputValue), [inputValue, getOptionFilters]);
  const wrappedSetSchemaOptions = useCallback(
    (newInputValue: string | string[], newSchemaOptions: Option[]) => {
      if (Array.isArray(newInputValue)) {
        // need to add shadow options so that multi pills actually render
        // these get filtered out of the dropdown
        const shadowOptions = newInputValue.map(wrappedGetOptionForValue);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setSchemaOptions(uniqBy('value', [...shadowOptions, ...newSchemaOptions]));
      } else {
        const { methodChain, partial } = parseWithPartialOption(newInputValue);
        const previousPath = methodChainToString(methodChain);
        const previousProp = partial || last(methodChain.links)?.name;

        const { schema: previousSchema } = schemaForFieldPath(previousPath, jsonSchema);
        const objectTypeAllowed =
          !allowedTypes || !allowedTypes.length || allowedTypes.includes(JsonSchemaTypes.Object);
        const showObjectSelect =
          canSelectObjects &&
          previousSchema &&
          'type' in previousSchema &&
          previousSchema.type === JsonSchemaTypes.Object &&
          objectTypeAllowed &&
          (allowedFieldPaths == null ||
            allowedFieldPaths.length === 0 ||
            (previousPath && allowedFieldPaths.includes(previousPath)));
        const selectObjectOption = showObjectSelect
          ? [
              {
                label: `Select ${previousProp} object`,
                value: `${previousPath}${ObjectKeySuffix}`,
                property: previousProp,
                schema: previousSchema,
              },
            ]
          : [];
        setSchemaOptions(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          compact(
            uniqBy('value', [
              ...selectObjectOption,
              ...newSchemaOptions,
              // When a valid option is selected, expanding the dropdown will show an empty menu.
              // We add the selected option manually so it displays.
              wrappedGetOptionForValue(newInputValue),
            ]),
          ),
        );
      }
    },
    [
      allowedFieldPaths,
      allowedTypes,
      canSelectObjects,
      jsonSchema,
      schemaForFieldPath,
      wrappedGetOptionForValue,
    ],
  );

  const updateMultiValue = useCallback(
    (newValue: string[]) => {
      const newOptionFilters = getOptionFilters(newValue);
      const newTopLevelOptions = getSchemaOptions({
        option: { schema: jsonSchema, methodChain: null },
        optionFilters: newOptionFilters,
        platformObjectJsonSchema,
        primitiveUtilities,
      });
      onValueChange(newValue);
      wrappedSetSchemaOptions(newValue, newTopLevelOptions);
      setShowDropdown(!!newTopLevelOptions.length);
      const newOptions: Option[] = newValue
        .map(wrappedGetOptionForValue)
        .filter((o): o is Option => !!o)
        .map((option) => ({
          ...option,
          value: vt.getTokenizedValue(option.value),
        }));
      onFieldSelect(newOptions);
    },
    [
      getOptionFilters,
      jsonSchema,
      platformObjectJsonSchema,
      primitiveUtilities,
      onValueChange,
      wrappedSetSchemaOptions,
      wrappedGetOptionForValue,
      onFieldSelect,
      vt,
    ],
  );

  const selectFinalField = useCallback(
    (option: Option) => {
      if (isMulti) {
        const newValue = [...(inputValue as string[]), option.value];
        updateMultiValue(newValue);
        setSearchValue('');
      } else {
        onValueChange(option.value);
        wrappedSetSchemaOptions(option.value, []);
        setShowDropdown(false);
        onFieldSelect({
          ...option,
          value: vt.getTokenizedValue(option.value),
        });
      }
    },
    [
      inputValue,
      isMulti,
      vt,
      updateMultiValue,
      wrappedSetSchemaOptions,
      onValueChange,
      onFieldSelect,
    ],
  );

  const parsePath = useCallback(
    (path: string) => {
      const { valid, schemaOptions, partial } = validatePath({
        path,
        jsonSchema,
        optionFilters,
        platformObjectJsonSchema,
        primitiveUtilities,
      });
      const optionMatch = schemaOptions.find(({ value }) => value === path);
      wrappedSetIsValid(valid);

      if (
        valid &&
        optionMatch &&
        !getSchemaOptions({
          option: optionMatch,
          optionFilters,
          platformObjectJsonSchema,
          primitiveUtilities,
        }).length
      ) {
        selectFinalField(optionMatch);
      } else {
        const filteredOptions = schemaOptions.filter(({ property }) => {
          return property.includes(partial);
        });
        wrappedSetSchemaOptions(isMulti ? inputValue : path, filteredOptions);
        setShowDropdown(true);
      }
    },
    [
      jsonSchema,
      optionFilters,
      platformObjectJsonSchema,
      primitiveUtilities,
      wrappedSetIsValid,
      selectFinalField,
      wrappedSetSchemaOptions,
      isMulti,
      inputValue,
    ],
  );

  const setSelectedOption = useCallback(
    (option: Option) => {
      wrappedSetIsValid(true);
      setSearchValue(option.value);
    },
    [wrappedSetIsValid],
  );

  const onOptionSelect = useCallback(
    (option: Option): void => {
      const schemaOptions = getSchemaOptions({
        option,
        optionFilters,
        platformObjectJsonSchema,
        primitiveUtilities,
      });
      let newInputValue;
      if (Array.isArray(inputValue)) {
        newInputValue = inputValue;
      } else if (schemaOptions.length === 0) {
        newInputValue = option.value;
      } else {
        newInputValue = `${option.value}.`;
      }
      wrappedSetSchemaOptions(newInputValue, [...schemaOptions, option]);
      setSelectedOption(option);

      // auto select if only 1 option
      if (!canSelectObjects && schemaOptions.length === 1) {
        return onOptionSelect(schemaOptions[0]);
      }

      if (schemaOptions.length === 0) {
        selectFinalField(option);
      } else {
        setSearchValue(`${option.value}.`);
      }
    },
    [
      optionFilters,
      platformObjectJsonSchema,
      primitiveUtilities,
      inputValue,
      wrappedSetSchemaOptions,
      setSelectedOption,
      canSelectObjects,
      selectFinalField,
    ],
  );

  const onObjectSelect = useCallback(
    (value: string) => {
      const option = wrappedGetOptionForValue(value);
      if (option) {
        setSelectedOption(option);
        selectFinalField(option);
      }
    },
    [selectFinalField, wrappedGetOptionForValue, setSelectedOption],
  );

  const onInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (e.relatedTarget && !closeDropdownOnBlur) {
        return;
      }
      onBlur?.();
      setShowDropdown(false);
      if (isMulti) {
        wrappedSetIsValid(inputValue.length ? !searchValue : !required);
      } else {
        wrappedSetIsValid(inputValue ? !searchValue || searchValue === inputValue : !required);
      }
    },
    [inputValue, searchValue, isMulti, required, onBlur, wrappedSetIsValid, closeDropdownOnBlur],
  );

  useEffect(() => {
    const options = getSchemaOptions({
      option: { schema: jsonSchema, methodChain: null },
      optionFilters,
      platformObjectJsonSchema,
      primitiveUtilities,
    });
    wrappedSetSchemaOptions(inputValue, options);
  }, []);

  useEffect(() => {
    if (isMulti) {
      return;
    }

    const newOptionFilters = getOptionFilters(inputValue);
    const newTopLevelOptions = getSchemaOptions({
      option: { schema: jsonSchema, methodChain: null },
      optionFilters: newOptionFilters,
      platformObjectJsonSchema,
      primitiveUtilities,
    });
    wrappedSetSchemaOptions(inputValue, newTopLevelOptions);
    setSearchValue(inputValue as string);
  }, [
    isMulti,
    inputValue,
    jsonSchema,
    getOptionFilters,
    wrappedSetSchemaOptions,
    platformObjectJsonSchema,
    primitiveUtilities,
  ]);

  const onInputFocus = () => {
    setShowDropdown(true);
    setIsValid(true);
    const currentInputValue = isMulti ? '' : (inputValue as string);
    setSearchValue(searchValue || currentInputValue);
  };

  const onChange = (val: string | string[]) => {
    if (isClearable && val === '') {
      setSearchValue('');
      onValueChange('');
      setIsValid(true);
      setShowDropdown(false);
      return;
    }

    if (Array.isArray(val)) {
      if (isMulti && val.length < inputValue.length) {
        // deleting value
        return updateMultiValue(val);
      }
      const newValue = last(val);
      if (newValue) {
        const newOption = wrappedGetOptionForValue(newValue);
        newOption && onOptionSelect(newOption);
      }
    } else if (val.endsWith(ObjectKeySuffix)) {
      onObjectSelect(val.replace(ObjectKeySuffix, ''));
    } else {
      const newOption = wrappedGetOptionForValue(val);
      newOption && onOptionSelect(newOption);
    }
  };

  const onInputChange = (val: string, e: { action: string }) => {
    if (e.action !== 'input-change') {
      return;
    }
    if (val === '' && !isMulti) {
      onValueChange('');
    }
    setSearchValue(val);
    parsePath(val);
  };

  const selectHasError = hasError || !isValid;

  return (
    <ReactSelect
      id={id}
      value={inputValue}
      autoFocus={autoFocus}
      inputValue={searchValue}
      hasError={selectHasError}
      selectHasError={selectHasError}
      disabled={disabled}
      isMulti={isMulti}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      isClearable={isClearable}
      isSearchable
      filterOption={(option, inputValue) => {
        if (option.value.includes(inputValue)) {
          return true;
        }
        if (inputValue.lastIndexOf('.') > 0) {
          // Replace the last instance of `.` with `['` to allow for the traversal of
          // object properties with special characters in the name.
          return option.value.includes(inputValue.replace(/\.([^.]*)$/, "['$1"));
        }
        return false;
      }}
      portal
      size={size}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      styles={{
        ...customStyles({ isClearable }),
        ...styles,
      }}
      menuIsOpen={showDropdown}
      options={schemaOptions}
      exampleData={exampleData}
      onInputChange={onInputChange}
      onChange={onChange}
      onFocus={onInputFocus}
      onBlur={onInputBlur}
      components={{
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        Option: SelectOption,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        Input: SelectInput,
      }}
    />
  );
};

export default JsonSchemaSelect;
